import { useCallback } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { PageContext } from "common";
import { useCortexContext } from "@via/context/cortex-context";

const toId = (value: string): string | number =>
  /^\d+$/.test(value) && Number.isSafeInteger(Number(value))
    ? Number(value)
    : value;

/**
 * Builds the page context the person may attach: the route, a title and the ids in the route,
 * nothing else. A file or the page's content is never attached (agent-6-2).
 */
export const useCurrentPageContext = (): (() => PageContext) => {
  const { pathname } = useLocation();
  const { getCurrentContext } = useCortexContext();
  return useCallback(() => {
    const handler = getCurrentContext();
    const params = handler
      ? (matchPath(handler.routePattern, pathname)?.params ?? {})
      : {};
    const ids: Record<string, string | number> = {};
    for (const [name, value] of Object.entries(params)) {
      if (typeof value === "string" && value.length > 0)
        ids[name] = toId(value);
    }
    return {
      route: pathname,
      title: handler?.contextName || document.title || pathname,
      ids,
    };
  }, [getCurrentContext, pathname]);
};

interface AttachContextButtonProps {
  attached: PageContext | null;
  disabled?: boolean;
  onAttach: (context: PageContext) => void;
  onRemove: () => void;
}

export const AttachContextButton = ({
  attached,
  disabled,
  onAttach,
  onRemove,
}: AttachContextButtonProps) => {
  const buildContext = useCurrentPageContext();
  if (attached) {
    return (
      <span
        data-testid="claude-attached-page"
        className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-50 px-2 py-1 text-xs text-indigo-700">
        <PaperClipIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{attached.title}</span>
        <button
          type="button"
          aria-label="Remove the attached page"
          onClick={onRemove}
          className="rounded-full p-0.5 hover:bg-indigo-100">
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onAttach(buildContext())}
      className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50">
      <PaperClipIcon className="h-3.5 w-3.5" />
      Attach this page
    </button>
  );
};
